/**
 * EcosystemEntityDetailPanel
 * 
 * Shows all linked data for a selected ecosystem entity:
 * relationships (parent paths + children), sources, offerings, event series, artifacts.
 * Provides action buttons for creating/linking within the ecosystem flow.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Building2, Database, GraduationCap, Calendar, FileText, Link2, Loader2, Plus,
  ChevronRight, Check, X, AlertTriangle, GitBranch,
} from 'lucide-react';
import { useState } from 'react';
import {
  useEcosystemEntityDetail, useEntityParentPaths,
  useRemoveRelationship, useApproveRelationship,
} from '@/hooks/useEcosystemData';
import {
  AFFILIATION_LABELS, RELATIONSHIP_TYPE_LABELS, RELATIONSHIP_TYPE_ICONS,
  type AffiliationRelationship, type EntityRelationshipType,
} from '@/types/ecosystem';
import { AddEditRelationshipDialog } from './AddEditRelationshipDialog';
import { toast } from 'sonner';

interface EcosystemEntityDetailPanelProps {
  entityId: string;
  entityName: string;
  onAddChild: () => void;
  onAddSource: () => void;
  onAddOffering: () => void;
  onAddSeries: () => void;
  onLinkArtifact: () => void;
  onLinkOffering: () => void;
}

export function EcosystemEntityDetailPanel({
  entityId,
  entityName,
  onAddChild,
  onAddSource,
  onAddOffering,
  onAddSeries,
  onLinkArtifact,
  onLinkOffering,
}: EcosystemEntityDetailPanelProps) {
  const { data: detail, isLoading } = useEcosystemEntityDetail(entityId);
  const { data: parentPaths } = useEntityParentPaths(entityId);
  const [showRelDialog, setShowRelDialog] = useState(false);

  const removeRelationship = useRemoveRelationship();
  const approveRelationship = useApproveRelationship();

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!detail) return null;

  const { counts, relationships } = detail;

  const handleRemove = async (relId: string) => {
    try {
      await removeRelationship.mutateAsync({ data: { relationship_id: relId } });
      toast.success('Relationship removed');
    } catch (e: any) {
      toast.error(e.message || 'Failed to remove');
    }
  };

  const handleApprove = async (relId: string) => {
    try {
      await approveRelationship.mutateAsync({ data: { relationship_id: relId } });
      toast.success('Relationship approved');
    } catch (e: any) {
      toast.error(e.message || 'Failed to approve');
    }
  };

  return (
    <div className="space-y-4 pb-4">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline" className="gap-1">
          <GitBranch className="h-3 w-3" /> {counts.relationships} relationships
        </Badge>
        <Badge variant="outline" className="gap-1">
          <Database className="h-3 w-3" /> {counts.sources} sources
        </Badge>
        <Badge variant="outline" className="gap-1">
          <GraduationCap className="h-3 w-3" /> {counts.offerings} offerings
        </Badge>
        <Badge variant="outline" className="gap-1">
          <Calendar className="h-3 w-3" /> {counts.eventSeries} event series
        </Badge>
        <Badge variant="outline" className="gap-1">
          <FileText className="h-3 w-3" /> {counts.artifacts} artifacts
        </Badge>
      </div>

      <div className="space-y-4">
        {/* Relationships Section */}
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Relationships</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setShowRelDialog(true)} className="gap-1 h-7">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </CardHeader>
          <CardContent className="pt-0 space-y-3">
            {/* Parent Paths (breadcrumbs) */}
            {parentPaths && parentPaths.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Parent Paths</p>
                <div className="space-y-1">
                  {parentPaths.map((path, idx) => (
                    <div key={idx} className="flex items-center gap-1 text-xs flex-wrap">
                      {path.map((seg, si) => (
                        <span key={si} className="flex items-center gap-0.5">
                          {si > 0 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
                          <span className="text-foreground">{seg.name}</span>
                          {RELATIONSHIP_TYPE_ICONS[seg.relType] && (
                            <span title={seg.relType}>{RELATIONSHIP_TYPE_ICONS[seg.relType]}</span>
                          )}
                        </span>
                      ))}
                      <ChevronRight className="h-3 w-3 text-muted-foreground" />
                      <span className="font-medium text-primary">{entityName}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Direct parent relationships */}
            {relationships.asChild.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Parents</p>
                <div className="space-y-1">
                  {relationships.asChild.map(rel => (
                    <div key={rel.id} className="flex items-center justify-between text-sm py-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {RELATIONSHIP_TYPE_ICONS[rel.relationship_type as EntityRelationshipType]}{' '}
                          {RELATIONSHIP_TYPE_LABELS[rel.relationship_type as EntityRelationshipType]}
                        </Badge>
                        <span>{rel.parent_name}</span>
                        {rel.is_primary && <Badge variant="outline" className="text-xs">Primary</Badge>}
                        {rel.needs_review && (
                          <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-400">
                            <AlertTriangle className="h-3 w-3 mr-0.5" /> Pending
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {rel.needs_review && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" title="Approve"
                            onClick={() => handleApprove(rel.id)}>
                            <Check className="h-3 w-3 text-green-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-6 w-6" title="Remove"
                          onClick={() => handleRemove(rel.id)}>
                          <X className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Children */}
            {relationships.asParent.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Children</p>
                <div className="space-y-1">
                  {relationships.asParent.map(rel => (
                    <div key={rel.id} className="flex items-center justify-between text-sm py-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="text-xs">
                          {RELATIONSHIP_TYPE_ICONS[rel.relationship_type as EntityRelationshipType]}{' '}
                          {RELATIONSHIP_TYPE_LABELS[rel.relationship_type as EntityRelationshipType]}
                        </Badge>
                        <span>{rel.child_name}</span>
                        {rel.needs_review && (
                          <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-400">
                            <AlertTriangle className="h-3 w-3 mr-0.5" /> Pending
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {rel.needs_review && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" title="Approve"
                            onClick={() => handleApprove(rel.id)}>
                            <Check className="h-3 w-3 text-green-600" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-6 w-6" title="Remove"
                          onClick={() => handleRemove(rel.id)}>
                          <X className="h-3 w-3 text-destructive" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {relationships.asChild.length === 0 && relationships.asParent.length === 0 && (
              <p className="text-xs text-muted-foreground">No relationships. Use "Add" to create one.</p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Linked Sources</CardTitle>
            <Button variant="ghost" size="sm" onClick={onAddSource} className="gap-1 h-7">
              <Plus className="h-3 w-3" /> Add Source
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {detail.sources.length === 0 ? (
              <p className="text-xs text-muted-foreground">No sources linked.</p>
            ) : (
              <div className="space-y-1">
                {detail.sources.map(src => (
                  <div key={src.id} className="flex items-center justify-between gap-2 text-sm py-1">
                    <span className="min-w-0 flex-1 truncate">{src.source_name || src.source_identifier}</span>
                    <Badge variant="outline" className="text-xs shrink-0">{src.source_type}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Offerings</CardTitle>
            <div className="flex gap-1">
              <Button variant="ghost" size="sm" onClick={onAddOffering} className="gap-1 h-7">
                <Plus className="h-3 w-3" /> Create
              </Button>
              <Button variant="ghost" size="sm" onClick={onLinkOffering} className="gap-1 h-7">
                <Link2 className="h-3 w-3" /> Link
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {detail.offerings.length === 0 ? (
              <p className="text-xs text-muted-foreground">No offerings linked.</p>
            ) : (
              <div className="space-y-1">
                {detail.offerings.map(off => (
                  <div key={off.id} className="flex items-center justify-between gap-2 text-sm py-1">
                    <span className="min-w-0 flex-1 truncate">
                      {off.start_datetime ? new Date(off.start_datetime).toLocaleDateString() : 'No date'}
                      {off.delivery_type && ` — ${off.delivery_type}`}
                    </span>
                    <Badge variant={off.status === 'completed' ? 'secondary' : 'outline'} className="text-xs shrink-0">
                      {off.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Event Series</CardTitle>
            <Button variant="ghost" size="sm" onClick={onAddSeries} className="gap-1 h-7">
              <Plus className="h-3 w-3" /> Add Series
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {detail.eventSeries.length === 0 ? (
              <p className="text-xs text-muted-foreground">No event series.</p>
            ) : (
              <div className="space-y-1">
                {detail.eventSeries.map(s => (
                  <div key={s.id} className="flex items-center justify-between gap-2 text-sm py-1">
                    <span className="min-w-0 flex-1 truncate font-medium">{s.series_name}</span>
                    <Badge variant="outline" className="text-xs shrink-0">{s.frequency}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-sm font-medium">Artifacts</CardTitle>
            <Button variant="ghost" size="sm" onClick={onLinkArtifact} className="gap-1 h-7">
              <Link2 className="h-3 w-3" /> Link
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {detail.artifacts.length === 0 ? (
              <p className="text-xs text-muted-foreground">No artifacts.</p>
            ) : (
              <div className="space-y-1">
                {detail.artifacts.map(a => (
                  <div key={a.id} className="flex items-center justify-between gap-2 text-sm py-1">
                    <span className="min-w-0 flex-1 truncate">{a.title || 'Untitled'}</span>
                    <Badge variant="outline" className="text-xs shrink-0">{a.artifact_type}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {detail.affiliations.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Affiliations</CardTitle>
            </CardHeader>
            <CardContent className="pt-0">
              <div className="space-y-1">
                {detail.affiliations.map((aff, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm py-1">
                    <Badge variant="secondary" className="text-xs">
                      {AFFILIATION_LABELS[aff.relationship_type as AffiliationRelationship] || aff.relationship_type}
                    </Badge>
                    <span>{aff.display_name || aff.provider_id.substring(0, 8)}</span>
                    {aff.notes && <span className="text-muted-foreground text-xs">({aff.notes})</span>}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <AddEditRelationshipDialog
        open={showRelDialog}
        onOpenChange={setShowRelDialog}
        entityId={entityId}
        entityName={entityName}
      />
    </div>
  );
}
